<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex mb-2 justify-content-end align-items-start">
                    <span class="flex-grow-1" v-if="name"><b>{{ $t('vouchers.voucherIntro') }}</b> {{name}}</span>
                </div>
                <div class="d-flex flex-column">
                    <!-- Code -->
                    <div role="group" class="form-group col-12">
                        <label class="">{{ $t('vouchers.codeMandatory') }}</label>
                        <CInput type="text" v-model="voucher.code" :invalid-feedback="errorOutput['Code']" :is-valid="genericValidator(voucher.code) && errorValidator('Code')"></CInput>
                    </div>
                    
                    <!-- Discount Type -->
                    <div role="group" class="form-group col-12">
                        <label class="">{{ $t('vouchers.discountTypeMandatory') }}</label>
                        <select class="form-control" v-model="voucher.isPercentage" :class="{'is-invalid': showValidation && voucher.isPercentage === null}">
                            <option :value="null">{{ $t('vouchers.selectType') }}</option>
                            <option :value="false">{{ $t('vouchers.amount') }}</option>
                            <option :value="true">{{ $t('vouchers.percentage') }}</option>
                        </select>
                        <div v-if="errorOutput['IsPercentage']" class="invalid-feedback">{{errorOutput['IsPercentage']}}</div>
                    </div>
                    
                    <!-- Value -->
                    <div role="group" class="form-group col-12">
                        <label class="">{{ $t('vouchers.valueMandatory') }}</label>
                        <CInput 
                            type="number" 
                            step="0.01" 
                            min="0" 
                            v-model="voucher.value" 
                            :invalid-feedback="errorOutput['Value']" 
                            :is-valid="genericValidator(voucher.value) && errorValidator('Value')">
                        </CInput>
                    </div>
                    
                    <!-- Experience -->
                    <div role="group" class="form-group col-12">
                        <label class="">{{ $t('vouchers.experienceMandatory') }}</label>
                        <select class="form-control" v-model="voucher.experienceId" :class="{'is-invalid': showValidation && !voucher.experienceId}" :disabled="experiencesLoading">
                            <option :value="null">{{ $t('vouchers.selectExperience') }}</option>
                            <option v-for="exp in experiences" :key="exp.id" :value="exp.id">{{ getExperienceName(exp) }}</option>
                        </select>
                        <div v-if="errorOutput['ExperienceId']" class="invalid-feedback">{{errorOutput['ExperienceId']}}</div>
                        <div v-if="experiencesLoading" class="text-muted small">A carregar experiências...</div>
                        <div v-if="!experiencesLoading && experiences.length === 0" class="text-warning small">Nenhuma experiência disponível</div>
                    </div>
                    
                    <!-- Max Uses -->
                    <div role="group" class="form-group col-12">
                        <label class="">{{ $t('vouchers.maxUses') }}</label>
                        <CInput 
                            type="number" 
                            min="1" 
                            v-model="voucher.maxUses" 
                            :invalid-feedback="errorOutput['MaxUses']" 
                            :is-valid="errorValidator('MaxUses')">
                        </CInput>
                    </div>
                    
                    <!-- Expiration Date -->
                    <div role="group" class="form-group col-12">
                        <label class="">{{ $t('vouchers.expirationDateOptional') }}</label>
                        <CInput 
                            type="date" 
                            v-model="voucher.expirationDate" 
                            :invalid-feedback="errorOutput['ExpirationDate']" 
                            :is-valid="errorValidator('ExpirationDate')">
                        </CInput>
                    </div>
                </div>
                <!-- Action button + submitting -->  
                <div role="group" class="form-group col-12 d-flex justify-content-end">
                    <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                    <CButton v-else type="button" class="btn btn-primary" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                </div>
                <!-- Loading -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>
 
<script>
import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
import { mapState, mapActions } from 'vuex'
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import FormLoading from '@/controls/FormLoading.vue'
import InputGetList from "@/classes/InputGetList";

export default {
  components: { ConfirmationPopUp, FormLoading },
  props:{    
    baseVoucher: {
      type: Object,
      default: null,
    },
    baseVoucherName: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      triedSubmit: false,
      voucher: {
        code: '',
        value: 0,
        isPercentage: null,
        maxUses: null,
        expirationDate: null,
        experienceId: null,
        touristId: null
      },
      submitting: false,
      showConfirmationPopup: false,
      confirmationMessage: "",
      showValidation: false,
      experiencesLoading: false
    }
  },
  mounted: function() {
    this.resetVoucher();
    this.loadExperiences();
  },
  activated: function() {
    // Reload experiences when component is activated (e.g., returning from another page)
    this.loadExperiences();
  },
  computed: {
    ...mapState({ 
      alertType: state => state.alert.type,
      alertOutput: state => state.alert.output
    }),
    ...mapState('experiences', ['getExperiencesOutput']),
    errorOutput: function() {
      var result = {};

      if(this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput)) {
        result = this.alertOutput.reduce(function(accum, currentVal) {
          accum[currentVal.key] = currentVal.value;
          return accum;
        }, {});
      }

      return result;
    },
    ...mapState('vouchers', ['postVoucherOutput']),
    ...mapState('vouchers', ['putVoucherOutput']),
    name: function() {
      return this.baseVoucher || this.baseVoucherName ? (this.baseVoucher ? this.baseVoucher.code : this.baseVoucherName) : null;
    },
    experiences: function() {
      const output = this.getExperiencesOutput;
      if (output && output.data && Array.isArray(output.data)) {
        // Map experiences to ensure we have name property
        return output.data.map(exp => ({
          id: exp.id,
          name: exp.name || (exp.names && (exp.names.pt || exp.names.en)) || `Experiência ${exp.id}`
        }));
      }
      return [];
    }
  },
  methods: {
    ...mapActions('experiences', ['getExperiences']),
    resetVoucher() {
      if(!this.baseVoucher) {
        this.voucher = {
          code: '',
          value: 0,
          isPercentage: null,
          maxUses: null,
          expirationDate: null,
          experienceId: null,
          touristId: null
        };
      } else {
        this.voucher = deepClone({}, this.baseVoucher);
        if (this.voucher.expirationDate) {
          // Convert to YYYY-MM-DD format for date input
          const date = new Date(this.voucher.expirationDate);
          this.voucher.expirationDate = date.toISOString().split('T')[0];
        }
      }
    },
    async loadExperiences() {
      try {
        this.experiencesLoading = true;
        const input = new InputGetList(1000, 1, null, 1, "id");
        await this.getExperiences(input);
        // Force reactivity update
        this.$nextTick(() => {
          this.$forceUpdate();
          this.experiencesLoading = false;
        });
      } catch (error) {
        console.error('Error loading experiences:', error);
        this.experiencesLoading = false;
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: 'Erro ao carregar experiências'
        });
      }
    },
    submitClicked() {
      this.triedSubmit = true;
      this.showValidation = true;
      
      if(this.isValid()) {
        this.submitting = true;
        const voucherData = {
          ...this.voucher,
          expirationDate: this.voucher.expirationDate ? new Date(this.voucher.expirationDate).toISOString() : null
        };
        this.$emit('submit-requested', voucherData);
      }
    },
    resetSubmitting() {
      this.submitting = false;
    },
    isValid() {
      return this.voucher.code && 
             this.voucher.isPercentage !== null && 
             this.voucher.value > 0 && 
             this.voucher.experienceId && 
             (this.voucher.maxUses === null || this.voucher.maxUses === undefined || this.voucher.maxUses > 0);
    },
    genericValidator,
    errorValidator,
    getExperienceName(exp) {
      if (exp.name) return exp.name;
      if (exp.names && exp.names.pt) return exp.names.pt;
      if (exp.names && exp.names.en) return exp.names.en;
      return 'Experiência ' + exp.id;
    },
    onClickConfirmationPopup(value) {
      this.showConfirmationPopup = value;     
      this.$router.push({name: "VouchersList" });
    },
  },
  watch: {
    putVoucherOutput: function(val) {
      if(val) {
        this.showConfirmationPopup = true;
        this.confirmationMessage = this.$t('vouchers.updateSuccess');
      }
    },
    postVoucherOutput: function(val) {
      if(val) {
        this.showConfirmationPopup = true;
        this.confirmationMessage = this.$t('vouchers.createSuccess');
      }
    },
    baseVoucher: function() {
      this.resetVoucher();
    },
    getExperiencesOutput: function(newVal) {
      // Force update when experiences are loaded
      if (newVal && newVal.data) {
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      }
    }
  }
}
</script>

